package ui.company.detail

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import api.CampaignType
import api.ResourceOrientation
import api.ResourceType
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.*
import org.jetbrains.compose.resources.StringResource
import provider.LocalHomeDrawerProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.DataState
import tools.EFieldRegularBoolean
import tools.GlobalObjectShare
import tools.TabEntry
import tools.rememberString
import ui.resource.ResourceListingArgs
import ui.widget.*

data class CompanyArgs(
    var companyId: Long = -1,
    var forceUpNavigation: Boolean = false
)

@ComposableNavNode(CompanyArgs::class)
@Composable
fun CompanyUI() {
    with(CompanyScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }

    }
}

@Composable
private fun CompanyScope.Content(nnvl: CompanyScope.Wrapper) {
    val deleteState = DialogState.remember()
    val saveState = nnvl.saveState()

    DeleteDialog(deleteState, nnvl.data)
    val toolbarActions = remember {
        buildList {
            if (nnvl.canDelete) {
                add(ToolbarAction.delete { deleteState.show() })
            }
            if (nnvl.canEdit) {
                add(ToolbarAction.save(saveState) { action(CompanyVM.Action.Save) })
            }
        }
    }

    Column(Modifier.fillMaxSize()) {
        when {
            login.permission.canCompany(Io.Permission.OTHERS_VIEW) -> {
                Toolbar(
                    nav = nav,
                    titlePrefix = Res.string.company_detail__title.rememberString(),
                    titleSuffix = "(${nnvl.data.id})",
                    actions = toolbarActions,
                )
            }

            display.isMobile -> {
                Toolbar(
                    nav = nav,
                    titlePrefix = Res.string.company_detail__title.rememberString(),
                    titleSuffix = "(${nnvl.data.id})",
                    actions = toolbarActions,
                    navigationIcon = {
                        LocalHomeDrawerProvider.current.MenuIcon {}
                    }
                )
            }

            args.forceUpNavigation -> {
                Toolbar(
                    nav = nav,
                    titlePrefix = Res.string.company_detail__title.rememberString(),
                    titleSuffix = "(${nnvl.data.id})",
                    actions = toolbarActions
                )
            }

            else -> {
                Toolbar(
                    nav = nav,
                    titlePrefix = Res.string.company_detail__title.rememberString(),
                    titleSuffix = "(${nnvl.data.id})",
                    actions = toolbarActions,
                    navigationIcon = {}
                )
            }
        }
        Row(Modifier.fillMaxSize()) {
            if (display.resolution.width > 800) {
                PanelLeft(nnvl, Modifier.width(384.dp).fillMaxHeight())
                if (vm.tabsScreenLarge.isNotEmpty()) {
                    Spacer(
                        Modifier.width(4.dp)
                            .fillMaxHeight()
                            .background(MaterialTheme.colorScheme.tertiaryContainer)
                    )
                    PanelRight(
                        nnvl = nnvl,
                        tabs = vm.tabsScreenLarge,
                        selection = vm.selectedTabLarge.collectAsState(),
                        modifier = Modifier.weight(1F)
                    )
                }
            } else {
                PanelRight(
                    nnvl = nnvl,
                    tabs = vm.tabsScreenSmall,
                    selection = vm.selectedTabSmall.collectAsState(),
                    modifier = Modifier.weight(1F)
                )
            }
        }
    }
}

@Composable
private fun CompanyScope.DeleteDialog(deleteState: DialogState, data: Io.Company) {

    Dialog(deleteState) {
        val containsDependency = remember {
            data.count_device != 0
                    || data.count_user != 0
                    || data.count_location != 1
        }
        val confirmButton: @Composable () -> Unit = {
            if (!containsDependency) {
                TextButton({
                    action(CompanyVM.Action.Delete)
                    deleteState.hide()
                }) { Text(Res.string.dialog__action_delete.rememberString()) }
            }
        }

        DialogUI(
            Modifier.align(Alignment.Center),
            onDismissRequest = { deleteState.hide() },
            title = { Text(Res.string.company_detail__delete_title.rememberString()) },
            confirmButton = confirmButton,
            dismissButton = { TextButton({ deleteState.hide() }) { Text(Res.string.dialog__action_cancel.rememberString()) } },
        ) {
            if (!containsDependency) {
                Text(Res.string.company_detail__delete_message.rememberString())
            } else {
                Text(Res.string.company_detail__delete_warn.rememberString())
            }
        }
    }
}

@Composable
private fun CompanyScope.PanelRight(
    modifier: Modifier,
    nnvl: CompanyScope.Wrapper,
    tabs: List<TabEntry<CompanyVM.TabType>>,
    selection: State<TabEntry<CompanyVM.TabType>?>
) {

    Column(modifier) {
        Crossfade(selection.value, modifier = Modifier.weight(1F)) {
            when (it?.tag) {
                CompanyVM.TabType.INFO -> {
                    PanelLeft(
                        nnvl = nnvl,
                        modifier = Modifier.fillMaxSize()
                    )
                }

                CompanyVM.TabType.DEVICES -> {
                    Box(Modifier.fillMaxSize()) {
                        NavigationChildCompanyContentDevice(Node.DeviceListingUI, it.args)
                    }
                }

                CompanyVM.TabType.LOCATION -> {
                    Box(Modifier.fillMaxSize()) {
                        NavigationChildCompanyContentLocation(Node.LocationListingUI, it.args)
                    }
                }

                CompanyVM.TabType.USER -> {
                    Box(Modifier.fillMaxSize()) {
                        NavigationChildCompanyContentUser(Node.UserListingUI, it.args)
                    }
                }

                CompanyVM.TabType.LAYER_TOP -> {
                    Box(Modifier.fillMaxSize()) {
                        NavigationChildCompanyContentLayerTop(Node.CampaignListingUI, it.args)
                    }
                }

                CompanyVM.TabType.LAYER_BOTTOM -> {
                    Box(Modifier.fillMaxSize()) {
                        NavigationChildCompanyContentLayerBottom(Node.AppAssignListingUI, it.args)
                    }
                }

                CompanyVM.TabType.MEDIA -> {
                    Box(Modifier.fillMaxSize()) {
                        NavigationChildCompanyContentMedia(Node.ResourceListingUI, it.args)
                    }
                }

                CompanyVM.TabType.DASHBOARD -> {
                    Box(Modifier.fillMaxSize()) {
                        NavigationChildCompanyContentDashboard(Node.DashboardUI, it.args)
                    }
                }

                null -> Unit
            }
        }
        if (tabs.size > 1) {
            if (display.isMobileOrSmall && tabs.size > 3) {
                ScrollableTabRow(
                    selectedTabIndex = tabs.indexOfFirst { it.tag == selection.value?.tag }
                        .takeIf { it > -1 }
                        ?: 0,
                    modifier = Modifier.fillMaxWidth(),
                    containerColor = MaterialTheme.colorScheme.surfaceContainer,
                    edgePadding = 0.dp
                ) {
                    tabs.forEach { tabDefinition ->
                        Tab(
                            selected = tabDefinition.tag == selection.value?.tag,
                            onClick = {
                                with(tabDefinition) { onClick(tabDefinition) }
                            },
                            unselectedContentColor = MaterialTheme.colorScheme.onSurface,
                            selectedContentColor = MaterialTheme.colorScheme.primary,
                            text = { Text(tabDefinition.label.rememberString()) },
                            icon = { Icon(tabDefinition.icon, null) })
                    }
                }
            } else {
                NavigationBar(Modifier.fillMaxWidth()) {
                    tabs.forEach { tabDefinition ->
                        NavigationBarItem(
                            selected = tabDefinition.tag == selection.value?.tag,
                            onClick = {
                                with(tabDefinition) { onClick(tabDefinition) }
                            },
                            label = { Text(tabDefinition.label.rememberString()) },
                            icon = { Icon(tabDefinition.icon, null) })
                    }
                }
            }
        }
    }
}

@Composable
private fun CompanyScope.PanelLeft(nnvl: CompanyScope.Wrapper, modifier: Modifier) {

    val scrollState = rememberScrollState()
    Box(
        modifier = modifier.fillMaxHeight()
            .padding(8.dp)
    ) {
        Column(
            Modifier.fillMaxHeight()
                .verticalScroll(scrollState)
        ) {
            CompanyName(nnvl)
            Space(24.dp)
            CampaignSetttings(nnvl)
            Space(24.dp)
            DefaultVisual(nnvl)
            Space(24.dp)
        }
    }
}

@Composable
private fun CompanyName(nnvl: CompanyScope.Wrapper) {
    TextField(
        value = nnvl.name.text,
        readOnly = !nnvl.canEdit || nnvl.data.main == 1,
        enabled = nnvl.data.main != 1,
        label = { Text(Res.string.company_detail__info__name_hint.rememberString()) },
        leadingIcon = { Icon(Svg.Format_text, null) },
        isError = nnvl.name.err,
        modifier = Modifier.fillMaxWidth(),
        onValueChange = {
            nnvl.name.set(it)
        })
}

@Composable
private fun CampaignSetttings(nnvl: CompanyScope.Wrapper) {
    val currentMode = remember {
        mutableStateOf(CompanyScope.SellMode.all.firstOrNull { it.type match nnvl.campaignType.state } ?: CompanyScope.SellMode.all.first())
    }
    Text(
        text = Res.string.company_detail__info__sell_mode.rememberString(),
        style = MaterialTheme.typography.labelSmall
    )
    HorizontalDivider(Modifier.fillMaxWidth())
    Space(8.dp)
    val canChangeSellMode = nnvl.canEdit && nnvl.data.campaign.canChange

    DropDownPickerCard(
        selected = currentMode,
        values = CompanyScope.SellMode.all,
        itemWidth = 360.dp,
        previewImage = Svg.Palette_swatch_variant,
        enabled = canChangeSellMode && nnvl.canAdd
    ) {
        nnvl.campaignType.set(it.type.type)
    }
    Space(8.dp)

    AnimatedVisibility(
        visible = CampaignType.SLOT match nnvl.campaignType.state,
        enter = fadeIn() + expandVertically(),
        exit = fadeOut() + shrinkVertically()
    ) {
        Column {
            TextField(
                value = nnvl.campaignItemCount.text,
                readOnly = !canChangeSellMode,
                label = { Text(Res.string.company_detail__info__slot_count.rememberString()) },
                leadingIcon = { Icon(Svg.List_box_outline, null) },
                isError = nnvl.campaignItemCount.err,
                modifier = Modifier.fillMaxWidth(),
                onValueChange = {
                    nnvl.campaignItemCount.set(it)
                })
            Space(8.dp)
            TextField(
                value = nnvl.campaignItemDuration.text,
                readOnly = !canChangeSellMode,
                label = { Text(Res.string.company_detail__info__slot_duration.rememberString()) },
                leadingIcon = { Icon(Svg.Timer_edit_outline, null) },
                isError = nnvl.campaignItemDuration.err,
                modifier = Modifier.fillMaxWidth(),
                onValueChange = {
                    nnvl.campaignItemDuration.set(it)
                })
        }
    }
    AnimatedVisibility(
        visible = CampaignType.IMPRESSION match nnvl.campaignType.state,
        enter = fadeIn() + expandVertically(),
        exit = fadeOut() + shrinkVertically()
    ) {
        Column {
            TextField(
                readOnly = !nnvl.canEdit,
                value = nnvl.campaignItemCount.text,
                label = { Text(Res.string.company_detail__info__impression_count.rememberString()) },
                leadingIcon = { Icon(Svg.List_box_outline, null) },
                isError = nnvl.campaignItemCount.err,
                modifier = Modifier.fillMaxWidth(),
                onValueChange = { if (nnvl.canEdit) nnvl.campaignItemCount.set(it) })
            Space(8.dp)
            TextField(
                readOnly = !nnvl.canEdit,
                value = nnvl.campaignItemDuration.text,
                label = { Text(Res.string.company_detail__info__impression_duration.rememberString()) },
                leadingIcon = { Icon(Svg.Timer_edit_outline, null) },
                isError = nnvl.campaignItemDuration.err,
                modifier = Modifier.fillMaxWidth(),
                onValueChange = { if (nnvl.canEdit) nnvl.campaignItemDuration.set(it) })
        }
    }
    AnimatedVisibility(
        visible = nnvl.campaignType.state != CampaignType.NONE.type,
        enter = fadeIn() + expandVertically(),
        exit = fadeOut() + shrinkVertically()
    ) {
        Column {
            SwitchRow(
                canEdit = nnvl.canEdit,
                label = Res.string.company_detail__info__campaign_commercial_enable,
                field = nnvl.enableCommercial
            )
            AnimatedVisibility(
                visible = nnvl.enableCommercial.state,
                enter = fadeIn() + expandVertically(),
                exit = fadeOut() + shrinkVertically()
            ) {
                SwitchRow(
                    canEdit = nnvl.canEdit,
                    label = Res.string.company_detail__info__campaign_commercial_as_non_commercial_enable,
                    field = nnvl.enableCommercialAsNonCommercial
                )
            }

            if (nnvl.canAdd) {
                SwitchRow(
                    canEdit = nnvl.canEdit,
                    label = Res.string.company_detail__info__campaign_ignore_visual_ratio,
                    field = nnvl.campaignResourcesIgnoreAspectRatio
                )
                SwitchRow(
                    canEdit = nnvl.canEdit,
                    label = Res.string.company_detail__info__campaign_ignore_visual_duration,
                    field = nnvl.campaignResourcesIgnoreDuration
                )
//                SwitchRow(
//                    canEdit = nnvl.canEdit,
//                    label = Res.string.company_detail__info__campaign_ignore_visual_orientation,
//                    field = nnvl.campaignResourcesIgnoreRotation
//                )
            }
        }
    }
}

@Composable
private fun SwitchRow(canEdit: Boolean, label: StringResource, field: EFieldRegularBoolean) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(top = 8.dp)
            .clip(CardDefaults.shape)
            .clickable(canEdit) { field.set(!field.state) }
            .padding(horizontal = 8.dp)
    ) {
        Text(
            text = label.rememberString(),
            modifier = Modifier.weight(1F).align(Alignment.CenterVertically)
        )
        IconicSwitch(
            checked = field.state,
            modifier = Modifier.align(Alignment.CenterVertically),
            onCheckedChange = { if (canEdit) field.set(it) })
    }
}

@Composable
private fun CompanyScope.DefaultVisual(nnvl: CompanyScope.Wrapper) {

    val imagePickerKeyLand = remember(args.companyId) {
        "CompanyUI:land:${args.companyId}"
    }

    val imagePickerKeyPort = remember(args.companyId) {
        "CompanyUI:port:${args.companyId}"
    }

    GlobalObjectShare.Consumer(imagePickerKeyLand, imagePickerKeyPort)

    fun openResourcePicker(key: String) {
        GlobalObjectShare.launchImagePicker(
            key = key,
            nav = nav,
            args = ResourceListingArgs(
                companyId = nnvl.data.id,
                restrictionType = ResourceType.IMAGE.mask,
                restrictionOrientation = when (key) {
                    imagePickerKeyLand -> ResourceOrientation.HORIZONTAL.mask
                    imagePickerKeyPort -> ResourceOrientation.VERTICAL.mask
                    else -> error("illegal orientation")
                }
            )
        ) {
            it.firstOrNull()?.apply {
                when (key) {
                    imagePickerKeyLand -> nnvl.defaultVisualLand.set(id)
                    imagePickerKeyPort -> nnvl.defaultVisualPort.set(id)
                }
            }
        }
    }

    Text(
        Res.string.company_detail__info__default_visual.rememberString(),
        style = MaterialTheme.typography.labelSmall
    )
    HorizontalDivider(Modifier.fillMaxWidth())
    Space(8.dp)
    Row(Modifier.width(368.dp)) {
        Card(
            onClick = { openResourcePicker(imagePickerKeyLand) },
            enabled = nnvl.canEdit,
            modifier = Modifier.weight(3.15F)
        ) {
            Box {
                LazyImage(
                    id = nnvl.defaultVisualLand.state,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.fillMaxWidth()
                        .height((((360F / 4.15F) * 3.15F) * (9F / 16F)).dp)
                )
                if (nnvl.canEdit) {
                    Icon(
                        imageVector = Svg.Edit,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onPrimary,
                        modifier = Modifier
                            .align(Alignment.BottomEnd)
                            .padding(4.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primary)
                            .padding(4.dp)
                    )
                }
            }
        }
        Space(8.dp)
        Card(
            onClick = { openResourcePicker(imagePickerKeyPort) },
            enabled = nnvl.canEdit,
            modifier = Modifier.weight(1F)
        ) {
            Box {
                LazyImage(
                    id = nnvl.defaultVisualPort.state,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.fillMaxWidth()
                        .height((((360F / 4.15F)) * (16F / 9F)).dp)
                )
                if (nnvl.canEdit) {
                    Icon(
                        imageVector = Svg.Edit,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onPrimary,
                        modifier = Modifier
                            .align(Alignment.BottomEnd)
                            .padding(4.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primary)
                            .padding(4.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun CompanyScope.Wrapper.saveState(): State<Boolean> {
    val state = remember {
        mutableStateOf(false)
    }
    LaunchedEffect(change) {
        state.value = change
    }

    return state
}
